import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { messageApi } from '../services/api';
import { MessageInboxOtherLocationUnread } from '../types';
import { useAuth } from './AuthContext';
import { useLocationContext } from './LocationContext';
import { usePermissions } from './PermissionsContext';

export const MESSAGES_UNREAD_CHANGED_EVENT = 'staffly:messages-unread-changed';

interface MessageUnreadContextType {
  unreadCount: number;
  otherLocationUnreadCount: number;
  otherLocations: MessageInboxOtherLocationUnread[];
  refreshUnreadCount: () => Promise<void>;
}

const MessageUnreadContext = createContext<MessageUnreadContextType>({
  unreadCount: 0,
  otherLocationUnreadCount: 0,
  otherLocations: [],
  refreshUnreadCount: async () => {},
});

export const useMessageUnread = () => useContext(MessageUnreadContext);

export const MessageUnreadProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { debouncedSelectedLocationId } = useLocationContext();
  const { loading: permissionsLoading, hasPermission, hasMessageManagement } = usePermissions();
  const canAccessMessages = hasPermission('my_messages') || hasMessageManagement;
  const [unreadCount, setUnreadCount] = useState(0);
  const [otherLocationUnreadCount, setOtherLocationUnreadCount] = useState(0);
  const [otherLocations, setOtherLocations] = useState<MessageInboxOtherLocationUnread[]>([]);

  const refreshUnreadCount = useCallback(async () => {
    if (!isAuthenticated || permissionsLoading || !canAccessMessages) {
      setUnreadCount(0);
      setOtherLocationUnreadCount(0);
      setOtherLocations([]);
      return;
    }
    try {
      const summary = await messageApi.getUnreadInboxCount(debouncedSelectedLocationId ?? undefined);
      setUnreadCount(summary.count);
      setOtherLocationUnreadCount(summary.otherLocationUnreadCount);
      setOtherLocations(summary.otherLocations);
    } catch {
      /* silent */
    }
  }, [isAuthenticated, permissionsLoading, canAccessMessages, debouncedSelectedLocationId]);

  useEffect(() => {
    void refreshUnreadCount();
  }, [refreshUnreadCount]);

  useEffect(() => {
    const onRefresh = () => {
      void refreshUnreadCount();
    };
    window.addEventListener(MESSAGES_UNREAD_CHANGED_EVENT, onRefresh);
    return () => window.removeEventListener(MESSAGES_UNREAD_CHANGED_EVENT, onRefresh);
  }, [refreshUnreadCount]);

  useEffect(() => {
    const onFocus = () => {
      void refreshUnreadCount();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshUnreadCount]);

  return (
    <MessageUnreadContext.Provider
      value={{ unreadCount, otherLocationUnreadCount, otherLocations, refreshUnreadCount }}
    >
      {children}
    </MessageUnreadContext.Provider>
  );
};
